import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ReactNode } from "react";

export type ModalSize = "small" | "medium" | "large" | "auto";

export interface Modal {
  id: string;
  isOpen: boolean;
  title?: string;
  content: ReactNode | null;
  size?: ModalSize;
}

export interface LayoutState {
  modals: Record<string, Modal>;
  actions: {
    openModal: (id: string, content: ReactNode, title?: string, size?: ModalSize) => void;
    closeModal: (id: string) => void;
    closeAllModals: () => void;
    isModalOpen: (id: string) => boolean;
    resetState: () => void;
  };
}

const initialState: Omit<LayoutState, "actions"> = {
  modals: {},
};

export const useLayoutStore = create<LayoutState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      actions: {
        openModal: (id, content, title, size = "medium") => {
          set(
            (state) => ({
              modals: {
                ...state.modals,
                [id]: {
                  id,
                  isOpen: true,
                  content,
                  title,
                  size,
                },
              },
            }),
            false,
            "openModal",
          );
        },

        closeModal: (id) => {
          set(
            (state) => ({
              modals: {
                ...state.modals,
                [id]: {
                  ...state.modals[id],
                  id,
                  isOpen: false,
                  content: null,
                },
              },
            }),
            false,
            "closeModal",
          );
        },

        closeAllModals: () => {
          set(
            (state) => ({
              modals: Object.keys(state.modals).reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: {
                    ...state.modals[key],
                    isOpen: false,
                    content: null,
                  },
                }),
                {},
              ),
            }),
            false,
            "closeAllModals",
          );
        },

        isModalOpen: (id) => {
          return get().modals[id]?.isOpen ?? false;
        },

        resetState: () => {
          set({ ...initialState }, false, "resetState");
        },
      },
    }),
    {
      name: "LayoutStore",
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useLayoutActions = () => useLayoutStore((state) => state.actions);
export const useModal = (id: string) =>
  useLayoutStore((state) => state.modals[id]);
export const useAllModals = () => useLayoutStore((state) => state.modals);
